import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
  type ReactNode,
} from "react";
import {
  ApiError,
  changePassword as apiChangePassword,
  deleteAccount as apiDeleteAccount,
  getMe,
  getPublicConfig,
  login as apiLogin,
  logout as apiLogout,
  refreshSession,
  register as apiRegister,
  removeAvatarPhoto as apiRemoveAvatarPhoto,
  setAccessToken,
  updateProfile as apiUpdateProfile,
  uploadAvatarPhoto as apiUploadAvatarPhoto,
  type LoginInput,
  type PasswordChangeInput,
  type ProfilePatch,
  type RegisterInput,
} from "../lib/api";
import { socket } from "../lib/socket";
import type { AccountProfile, ProtocolError, PublicServerConfig } from "../types/protocol";

/** Le jeu ne doit jamais attendre l'API : au-delà, on bascule en invité. */
const BOOT_TIMEOUT = 3_000;
/** Marge de sécurité pour ne pas planifier un rafraîchissement en boucle serrée. */
const MIN_REFRESH_DELAY = 15_000;

export type AuthStatus = "loading" | "guest" | "authenticated";
export type AuthPending = "register" | "login" | "logout" | "profile" | "password" | "delete";

export interface AuthContextValue {
  status: AuthStatus;
  user: AccountProfile | null;
  serverConfig: PublicServerConfig | null;
  accountsEnabled: boolean;
  error: ProtocolError | null;
  pending: AuthPending | null;
  register: (input: RegisterInput) => Promise<boolean>;
  login: (input: LoginInput) => Promise<boolean>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  updateProfile: (patch: ProfilePatch) => Promise<boolean>;
  /** Les octets recadrés par le navigateur ; le serveur les réencode avant de les garder. */
  uploadAvatarPhoto: (photo: Blob) => Promise<boolean>;
  removeAvatarPhoto: () => Promise<boolean>;
  changePassword: (input: PasswordChangeInput) => Promise<boolean>;
  deleteAccount: (password: string) => Promise<boolean>;
  dismissError: () => void;
}

const toProtocolError = (error: unknown): ProtocolError => {
  if (error instanceof ApiError) {
    return error.details === undefined
      ? { code: error.code, message: error.message }
      : { code: error.code, message: error.message, details: error.details };
  }
  return {
    code: "NETWORK_ERROR",
    message: "Le serveur est injoignable. Vérifie ta connexion.",
  };
};

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<AccountProfile | null>(null);
  const [serverConfig, setServerConfig] = useState<PublicServerConfig | null>(null);
  const [error, setError] = useState<ProtocolError | null>(null);
  const [pending, setPending] = useState<AuthPending | null>(null);
  const refreshTimer = useRef<number | null>(null);
  const bootStarted = useRef(false);
  const mounted = useRef(true);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimer.current !== null) {
      window.clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
  }, []);

  const forgetSession = useCallback(() => {
    clearRefreshTimer();
    setAccessToken(null);
    setUser(null);
    setStatus("guest");
  }, [clearRefreshTimer]);

  const silentRefresh = useRef<() => Promise<void>>(async () => {});

  const acceptSession = useCallback(
    (profile: AccountProfile, expiresIn: number) => {
      setUser(profile);
      setStatus("authenticated");
      clearRefreshTimer();
      const delay = Math.max(MIN_REFRESH_DELAY, Math.floor(expiresIn * 800));
      refreshTimer.current = window.setTimeout(() => {
        void silentRefresh.current();
      }, delay);
    },
    [clearRefreshTimer],
  );

  silentRefresh.current = async () => {
    try {
      const session = await refreshSession();
      if (!mounted.current) return;
      acceptSession(session.user, session.expiresIn);
    } catch {
      if (mounted.current) forgetSession();
    }
  };

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      clearRefreshTimer();
    };
  }, [clearRefreshTimer]);

  useEffect(() => {
    const giveUp = window.setTimeout(() => {
      setStatus((current) => (current === "loading" ? "guest" : current));
    }, BOOT_TIMEOUT);
    return () => window.clearTimeout(giveUp);
  }, []);

  useEffect(() => {
    // Le double montage de StrictMode ne doit pas consommer deux fois le cookie,
    // qui pivote à chaque rafraîchissement.
    if (bootStarted.current) return;
    bootStarted.current = true;

    void (async () => {
      try {
        const config = await getPublicConfig();
        setServerConfig(config);
        if (!config.accountsEnabled) {
          setStatus("guest");
          return;
        }
        // Une session absente est le cas normal d'un visiteur : aucun message d'erreur.
        const session = await refreshSession();
        acceptSession(session.user, session.expiresIn);
      } catch {
        setStatus("guest");
      }
    })();
  }, [acceptSession]);

  useEffect(() => {
    const onExpired = () => {
      void silentRefresh.current();
    };
    socket.on("auth:expired", onExpired);
    return () => {
      socket.off("auth:expired", onExpired);
    };
  }, []);

  const run = useCallback(
    async (action: AuthPending, task: () => Promise<void>): Promise<boolean> => {
      setPending(action);
      setError(null);
      try {
        await task();
        return true;
      } catch (cause: unknown) {
        if (mounted.current) setError(toProtocolError(cause));
        return false;
      } finally {
        if (mounted.current) setPending(null);
      }
    },
    [],
  );

  const register = useCallback(
    (input: RegisterInput) =>
      run("register", async () => {
        const session = await apiRegister(input);
        acceptSession(session.user, session.expiresIn);
      }),
    [acceptSession, run],
  );

  const login = useCallback(
    (input: LoginInput) =>
      run("login", async () => {
        const session = await apiLogin(input);
        acceptSession(session.user, session.expiresIn);
      }),
    [acceptSession, run],
  );

  const logout = useCallback(async () => {
    setPending("logout");
    try {
      await apiLogout();
    } catch {
      // Une déconnexion échouée côté réseau ne doit pas retenir l'utilisateur.
    } finally {
      if (mounted.current) {
        setPending(null);
        setError(null);
        forgetSession();
      }
    }
  }, [forgetSession]);

  const refreshUser = useCallback(async () => {
    try {
      const profile = await getMe();
      if (mounted.current) setUser(profile);
    } catch (cause: unknown) {
      if (cause instanceof ApiError && cause.status === 401) forgetSession();
    }
  }, [forgetSession]);

  const updateProfile = useCallback(
    (patch: ProfilePatch) =>
      run("profile", async () => {
        const profile = await apiUpdateProfile(patch);
        if (mounted.current) setUser(profile);
      }),
    [run],
  );

  const uploadAvatarPhoto = useCallback(
    (photo: Blob) =>
      run("profile", async () => {
        const profile = await apiUploadAvatarPhoto(photo);
        if (mounted.current) setUser(profile);
      }),
    [run],
  );

  const removeAvatarPhoto = useCallback(
    () =>
      run("profile", async () => {
        const profile = await apiRemoveAvatarPhoto();
        if (mounted.current) setUser(profile);
      }),
    [run],
  );

  const changePassword = useCallback(
    (input: PasswordChangeInput) => run("password", () => apiChangePassword(input)),
    [run],
  );

  const deleteAccount = useCallback(
    (password: string) =>
      run("delete", async () => {
        await apiDeleteAccount(password);
        forgetSession();
      }),
    [forgetSession, run],
  );

  const dismissError = useCallback(() => setError(null), []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      serverConfig,
      accountsEnabled: serverConfig?.accountsEnabled ?? false,
      error,
      pending,
      register,
      login,
      logout,
      refreshUser,
      updateProfile,
      uploadAvatarPhoto,
      removeAvatarPhoto,
      changePassword,
      deleteAccount,
      dismissError,
    }),
    [
      changePassword,
      deleteAccount,
      dismissError,
      error,
      login,
      logout,
      pending,
      refreshUser,
      register,
      removeAvatarPhoto,
      serverConfig,
      status,
      updateProfile,
      uploadAvatarPhoto,
      user,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth doit être utilisé dans un AuthProvider.");
  return value;
};
